import React, { useState, useContext } from 'react';
import { Box, Typography, Link, TextButton, styled } from 'bonfire-kindling';
import Axios from 'helpers/axios';
import { Loader } from 'components';
import { ToastContext } from 'contexts';
import { Trans, useTranslation } from 'react-i18next';
import { countries } from 'constants/regions';
import { STRIPECUSTOMERPORTALURL } from 'constants/apis';

const StyledRenewButton = styled(TextButton)(({ theme }) => ({
  background: theme.palette.primary.main,
  paddingTop: 0,
  paddingBottom: 0,
}));

const StyledEditButton = styled(TextButton)(({ theme }) => ({
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  color: theme.palette.primary.main,
}));

// Formats a Stripe billing address.
const BillingAddress = ({ stripeAddress }) => {
  if (stripeAddress === null) {
    return null;
  }

  const match = countries.find(({ value }) => value === stripeAddress.country);

  const countryLabel = match !== undefined ? match.label : stripeAddress.country;

  return (
    <>
      <Typography>{stripeAddress.line1}</Typography>
      {stripeAddress.line2 === '' || stripeAddress.line2 === null ? null : (
        <Typography>{stripeAddress.line2}</Typography>
      )}
      <Typography>
        {stripeAddress.city}, {stripeAddress.state} {stripeAddress.postal_code}
      </Typography>
      <Typography>{countryLabel}</Typography>
    </>
  );
};

export default ({ customerDetails, stripeSubscription, onSubmitHasAutoRenewal }) => {
  const { t } = useTranslation();
  const toastContext = useContext(ToastContext);
  const [isChangingAutoRenew, setIsChangingAutoRenew] = useState(false);

  if (!customerDetails || !stripeSubscription) {
    return <Loader variant="puff" isCentered />;
  }

  const handleChangeAutoRenew = hasAutoRenew => {
    setIsChangingAutoRenew(true);
    return onSubmitHasAutoRenewal(hasAutoRenew)
      .catch(() =>
        toastContext.triggerToast({ message: t('settings.subscription.auto_renew_error') })
      )
      .then(() => setIsChangingAutoRenew(false));
  };

  const goToStripeCustomerPortal = async () => {
    try {
      const { data } = await Axios.get(STRIPECUSTOMERPORTALURL);
      window.location.assign(data.url);
    } catch (err) {
      toastContext.triggerToast({ message: t('generic_error') });
    }
  };

  const handleClickEditBillingAddress = () => {
    goToStripeCustomerPortal();
  };
  const handleClickEditPaymentMethod = () => {
    goToStripeCustomerPortal();
  };

  const autoRenewDate = new Date(stripeSubscription.current_period_end * 1000).toDateString();

  if (stripeSubscription.cancel_at_period_end) {
    return (
      <>
        <Box mb={3}>
          <StyledRenewButton
            variant="contained"
            color="primary"
            onClick={() => handleChangeAutoRenew(true)}
            isLoading={isChangingAutoRenew}
          >
            {t('settings.subscription.resume')}
          </StyledRenewButton>
        </Box>
        <Typography variant="body1">
          {t('settings.subscription.resume_message')}
          <strong>{autoRenewDate}</strong>
        </Typography>
      </>
    );
  }

  return (
    <>
      <Box mb={3}>
        <Box display="flex" mb={1}>
          <Typography variant="h3" component="h3">
            {t('billing_info.title')}
          </Typography>
          <Box ml={1}>
            <StyledEditButton
              role="link"
              variant="text"
              onClick={handleClickEditBillingAddress}
              aria-label="Edit Billing Address"
              label="Edit"
            />
          </Box>
        </Box>
        <BillingAddress stripeAddress={customerDetails.address} />
      </Box>
      <Box mb={3}>
        <Box display="flex" mb={1}>
          <Typography variant="h3" component="h3">
            {t('settings.subscription.payment_method')}
          </Typography>
          <Box ml={1}>
            <StyledEditButton
              role="link"
              variant="text"
              onClick={handleClickEditPaymentMethod}
              aria-label="Edit Payment Method"
              label="Edit"
            />
          </Box>
        </Box>
        <Box>
          {customerDetails.paymentMethod ? (
            <>
              <Typography>{customerDetails.name}</Typography>
              <Typography>{`**** **** **** ${customerDetails.paymentMethod.last4}`}</Typography>
              <Typography>
                {t('settings.subscription.billing_message', {
                  amount: stripeSubscription.plan.amount / 100,
                  currency: stripeSubscription.plan.currency.toUpperCase(),
                  interval: stripeSubscription.plan.interval,
                })}
              </Typography>
            </>
          ) : (
            <Typography>{t('settings.subscription.add_payment_method')}</Typography>
          )}
        </Box>
      </Box>
      <Box display="flex" mb={3}>
        <Typography>{t('settings.subscription.renew_date', { date: autoRenewDate })}</Typography>
      </Box>
      <Box>
        <Trans i18nKey="settings.subscription.billing_update_message">
          <Typography display="inline">
            If you have any questions about your subscription, please reach out to the Bonfire
            support at
          </Typography>
          <Link href="mailto:support@gobonfire.com" rel="noopener noreferrer">
            support@gobonfire.com
          </Link>
        </Trans>
      </Box>
    </>
  );
};
